/**
 * LR Direct ATLAS Voice Post — deterministic transcript lane fallback.
 *
 * The voice-post model sometimes returns a null origin/destination even when
 * the speaker clearly said "<City, ST> to <City, ST>" (observed: "McAllen TX
 * to Bronx NY" came back with no destination). This parses the lane straight
 * out of the transcript so the missing side can be filled in. Values still go
 * through `search_locations` geocoding and stay editable in the confirm chips.
 */

const STATE_NAME_TO_CODE: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA",
  kansas: "KS", kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS",
  missouri: "MO", montana: "MT", nebraska: "NE", nevada: "NV",
  "new hampshire": "NH", "new jersey": "NJ", "new mexico": "NM", "new york": "NY",
  "north carolina": "NC", "north dakota": "ND", ohio: "OH", oklahoma: "OK",
  oregon: "OR", pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT",
  virginia: "VA", washington: "WA", "west virginia": "WV", wisconsin: "WI",
  wyoming: "WY", "district of columbia": "DC",
};

const STATE_CODES = new Set(Object.values(STATE_NAME_TO_CODE));

/** Boroughs the model tends to drop or mislabel. */
const BOROUGHS: Record<string, string> = {
  bronx: "NY", "the bronx": "NY", brooklyn: "NY", queens: "NY",
  "staten island": "NY", manhattan: "NY",
};

const STATE_ALT = [
  ...Object.keys(STATE_NAME_TO_CODE).sort((a, b) => b.length - a.length),
  ...Array.from(STATE_CODES),
].join("|");

/** Words that end a lane phrase (equipment / details spoken after the city). */
const TAIL_STOP =
  /\b(van|dry van|reefer|reefers|flatbed|step deck|stepdeck|power only|conestoga|lowboy|tanker|hopper|intermodal|hotshot|lbs|pounds|pound|today|tomorrow|monday|tuesday|wednesday|thursday|friday|saturday|sunday|next|private|open|market|network|rate|dollars?)\b/i;

const LEAD_FILLER =
  /^(?:.*?\b(?:post(?:ing)?|load|quote|request|shipment|pick(?:ing)?\s*up|from|need|i\s+have|got|move|haul)\b[\s,]*)+/i;

export interface LaneSide {
  city: string;
  state: string;
}

function normCity(raw: string): string {
  const c = raw.trim().replace(/^the\s+/i, "").replace(/[.,]+$/, "").trim();
  return c
    .split(/\s+/)
    .map((w) => (w.length > 2 ? w[0].toUpperCase() + w.slice(1) : w.toUpperCase()))
    .join(" ");
}

function toCode(state: string): string {
  const s = state.trim().toLowerCase();
  return STATE_NAME_TO_CODE[s] || s.toUpperCase();
}

/** Trailing "<city>[,] <state>" at the end of the pre-"to" segment. */
function parseLeft(segment: string): LaneSide | null {
  const cleaned = segment.replace(LEAD_FILLER, "");
  const m = new RegExp(`([A-Za-z][A-Za-z .'\\-]*?)[,]?\\s+(${STATE_ALT})\\s*$`, "i").exec(cleaned.trim());
  if (m) return { city: normCity(m[1]), state: toCode(m[2]) };
  return parseBorough(cleaned, /\b([A-Za-z .'-]+)\s*$/);
}

/** Leading "<city>[,] <state>" at the start of the post-"to" segment. */
function parseRight(segment: string): LaneSide | null {
  const cleaned = segment.trim();
  const m = new RegExp(`^([A-Za-z][A-Za-z .'\\-]*?)[,]?\\s+(${STATE_ALT})\\b`, "i").exec(cleaned);
  if (m) return { city: normCity(m[1]), state: toCode(m[2]) };
  return parseBorough(cleaned, /^\s*([A-Za-z .'-]+)/);
}

/** No state spoken, but a known borough name is present. */
function parseBorough(segment: string, pick: RegExp): LaneSide | null {
  const raw = pick.exec(segment)?.[1] || "";
  const chunk = raw.split(TAIL_STOP)[0].trim().toLowerCase().replace(/[.,]+$/, "");
  for (const key of Object.keys(BOROUGHS)) {
    if (new RegExp(`(^|\\s)${key}(\\s|$)`).test(chunk)) {
      return { city: normCity(key.replace(/^the\s+/, "")), state: BOROUGHS[key] };
    }
  }
  return null;
}

/**
 * Parse "<origin> to <destination>" out of a spoken transcript.
 * Returns whichever sides could be read; never throws.
 */
export function extractLaneFromTranscript(transcript: string): {
  origin: LaneSide | null;
  destination: LaneSide | null;
} {
  const empty = { origin: null, destination: null };
  if (!transcript) return empty;

  // Walk the "to" occurrences from last to first and keep the first split that
  // actually yields a place, so routing phrases ("send to my private network")
  // and filler ("a load to go from A to B") don't hijack the lane.
  const matches = [...transcript.matchAll(/\s+(?:to|going to|over to|into)\s+/gi)];
  for (let i = matches.length - 1; i >= 0; i--) {
    const m = matches[i];
    const at = m.index ?? 0;
    const left = transcript.slice(0, at);
    const rightRaw = transcript.slice(at + m[0].length);
    if (/^(?:my|the|our|open|all|a|an)\b/i.test(rightRaw.trim())) continue;
    // Trim the details spoken after the destination ("..., van, 42,000 lbs").
    const stop = TAIL_STOP.exec(rightRaw);
    const right = stop && stop.index > 0 ? rightRaw.slice(0, stop.index) : rightRaw;
    const destination = parseRight(right);
    if (!destination) continue;
    return { origin: parseLeft(left), destination };
  }
  return empty;
}

